import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiClient from '../../api/client';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { ArrowLeft, Calendar, Users, Clock, CheckCircle, AlertTriangle, Search, Trash2, UserPlus, XCircle, UserCheck, UserX, RotateCcw, History } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { fmtKstDateTime } from '../../lib/datetime';

const SessionDetail = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [sessionData, setSessionData] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [historyEvents, setHistoryEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // 수업 소개(description) 인라인 편집
  const [descEditing, setDescEditing] = useState(false);
  const [descDraft, setDescDraft] = useState('');
  const [descSaving, setDescSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [checkInDialog, setCheckInDialog] = useState(false);
  const [checkInInput, setCheckInInput] = useState('');
  const [settlementDialog, setSettlementDialog] = useState(false);
  const [settlementResult, setSettlementResult] = useState(null);
  const [addReservationDialog, setAddReservationDialog] = useState(false);
  const [memberSearchQuery, setMemberSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedPass, setSelectedPass] = useState(null);
  const [searching, setSearching] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteScope, setDeleteScope] = useState('single'); // 'single' | 'series'
  const [seriesPreview, setSeriesPreview] = useState(null);

  useEffect(() => {
    fetchSessionData();
  }, [sessionId]);

  const fetchSessionData = async () => {
    try {
      setLoading(true);
      const [sessionRes, reservationsRes, historyRes] = await Promise.all([
        apiClient.get(`/admin/sessions/${sessionId}`),
        apiClient.get(`/admin/sessions/${sessionId}/reservations`),
        apiClient.get(`/admin/sessions/${sessionId}/history`).catch(() => ({ data: [] })),
      ]);

      setSessionData(sessionRes.data);
      setReservations(reservationsRes.data);
      setHistoryEvents(historyRes.data || []);
    } catch (error) {
      console.error('Failed to fetch session data:', error);
      toast.error('세션 정보 조회 실패');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckIn = async (memberIdOrNumber) => {
    try {
      const payload = {};
      if (memberIdOrNumber.startsWith('M')) {
        payload.member_number = memberIdOrNumber;
      } else {
        payload.member_id = memberIdOrNumber;
      }
      payload.mode = 'staff';

      await apiClient.post(`/admin/sessions/${sessionId}/check-in`, payload);
      toast.success('출석 체크 완료');
      setCheckInDialog(false);
      setCheckInInput('');
      fetchSessionData();
    } catch (error) {
      console.error('Check-in failed:', error);
      toast.error(error.response?.data?.detail || '출석 체크 실패');
    }
  };

  const handleRevertAttendance = async (attendanceId) => {
    if (!window.confirm('출석을 취소하고 차감을 환불하시겠습니까?')) {
      return;
    }

    try {
      await apiClient.post(`/admin/attendances/${attendanceId}/revert`);
      toast.success('출석 취소 및 환불 완료');
      fetchSessionData();
    } catch (error) {
      console.error('Revert failed:', error);
      toast.error(error.response?.data?.detail || '출석 취소 실패');
    }
  };

  const handleRefundNoShow = async (reservationId, passId) => {
    if (!window.confirm('노쇼 차감을 환불하시겠습니까?')) {
      return;
    }

    try {
      await apiClient.post(`/admin/reservations/${reservationId}/refund`);
      toast.success('노쇼 차감 환불 완료');
      fetchSessionData();
    } catch (error) {
      console.error('Refund failed:', error);
      toast.error(error.response?.data?.detail || '환불 처리 실패');
    }
  };

  const handleCancelReservation = async (reservationId) => {
    if (!window.confirm('이 예약을 취소하시겠습니까? 취소된 예약은 목록에서 사라집니다.')) {
      return;
    }

    try {
      await apiClient.delete(`/admin/sessions/${sessionId}/reservations/${reservationId}`);
      toast.success('예약이 취소되었습니다');
      fetchSessionData();
    } catch (error) {
      console.error('Cancel reservation failed:', error);
      toast.error(error.response?.data?.detail || '예약 취소 실패');
    }
  };

  const handleSearchMembers = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      const response = await apiClient.get('/admin/members/search', {
        params: { q: query }
      });
      setSearchResults(response.data);
    } catch (error) {
      console.error('Member search failed:', error);
      toast.error('회원 검색 실패');
    } finally {
      setSearching(false);
    }
  };

  const handleAddReservation = async () => {
    if (!selectedMember || !selectedPass) {
      toast.error('회원과 이용권을 선택해주세요');
      return;
    }

    try {
      await apiClient.post(`/admin/sessions/${sessionId}/reservations`, {
        member_id: selectedMember._id,
        pass_id: selectedPass._id
      });
      
      toast.success('예약이 추가되었습니다');
      setAddReservationDialog(false);
      setMemberSearchQuery('');
      setSearchResults([]);
      setSelectedMember(null);
      setSelectedPass(null);
      fetchSessionData();
    } catch (error) {
      console.error('Add reservation failed:', error);
      toast.error(error.response?.data?.detail || '예약 추가 실패');
    }
  };

  const openDeleteDialog = async () => {
    setDeleteScope('single');
    setSeriesPreview(null);
    setDeleteDialog(true);
    try {
      const res = await apiClient.get(`/admin/sessions/${sessionId}/future-series`);
      setSeriesPreview(res.data);
    } catch (error) {
      console.error('Failed to fetch future series:', error);
    }
  };

  const handleDeleteSession = async () => {
    try {
      setDeleting(true);
      if (deleteScope === 'series') {
        const res = await apiClient.delete(`/admin/sessions/${sessionId}/future-series`);
        const { deleted_count, skipped_count } = res.data;
        const skipMsg = skipped_count > 0 ? ` (예약자가 있는 ${skipped_count}개 제외)` : '';
        toast.success(`${deleted_count}개의 수업이 삭제되었습니다${skipMsg}`);
      } else {
        await apiClient.delete(`/admin/sessions/${sessionId}`);
        toast.success('수업이 삭제되었습니다');
      }
      setDeleteDialog(false);
      navigate('/admin/schedule');
    } catch (error) {
      console.error('Delete session failed:', error);
      toast.error(error.response?.data?.detail || '수업 삭제 실패');
      setDeleting(false);
    }
  };

  const handleNoShowSettlement = async () => {
    try {
      const response = await apiClient.post('/admin/no-show-settlement', {
        session_id: sessionId
      });
      
      setSettlementResult(response.data);
      toast.success(`노쇼 정산 완료: ${response.data.processed}명 처리됨`);
      fetchSessionData();
    } catch (error) {
      console.error('Settlement failed:', error);
      toast.error(error.response?.data?.detail || '노쇼 정산 실패');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  if (!sessionData) {
    return <div className="p-8">세션을 찾을 수 없습니다.</div>;
  }

  const { session, instructor } = sessionData;
  const filteredReservations = searchQuery
    ? reservations.filter(r => 
        r.member?.name.includes(searchQuery) ||
        r.member?.member_number.includes(searchQuery) ||
        r.member?.phone.includes(searchQuery)
      )
    : reservations;

  const attendedCount = reservations.filter(r => r.settlement_status === 'deducted').length;
  const pendingCount = reservations.filter(r => r.settlement_status === 'pending').length;
  const noShowCount = reservations.filter(r => r.status === 'no_show').length;

  const startEditDesc = () => {
    setDescDraft(session.notes || '');
    setDescEditing(true);
  };
  const cancelEditDesc = () => {
    setDescDraft('');
    setDescEditing(false);
  };
  const saveDesc = async () => {
    try {
      setDescSaving(true);
      await apiClient.patch(`/admin/sessions/${sessionId}`, { description: descDraft });
      toast.success('수업 소개가 저장되었습니다');
      setDescEditing(false);
      await fetchSessionData();
    } catch (err) {
      toast.error(err.response?.data?.detail || '저장 실패');
    } finally {
      setDescSaving(false);
    }
  };

  return (
    <div className="p-8">
      <Button variant="ghost" onClick={() => navigate('/admin/schedule')} className="mb-4">
        <ArrowLeft className="mr-2 h-4 w-4" />
        스케줄로 돌아가기
      </Button>

      {/* Session Info Card */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-2 flex-wrap">
              {session.session_type === 'community' && (
                <span
                  className="inline-flex items-center gap-1 bg-orange-500 text-white text-[10px] font-bold px-2 py-1 rounded uppercase tracking-wider"
                  data-testid="community-badge"
                >
                  ⭐ Community
                </span>
              )}
              <CardTitle className="text-2xl">{session.snapshot_name}</CardTitle>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="text-gray-500 hover:text-red-600 hover:bg-red-50 shrink-0"
              onClick={openDeleteDialog}
              data-testid="delete-session-btn"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              수업 삭제
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <div className="flex items-center text-sm text-gray-600 mb-1">
                <Calendar className="h-4 w-4 mr-1" />
                날짜/시간
              </div>
              <div className="font-medium">
                {new Date(session.scheduled_at).toLocaleString('ko-KR', {
                  timeZone: 'Asia/Seoul',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </div>
            </div>
            <div>
              <div className="flex items-center text-sm text-gray-600 mb-1">
                <Users className="h-4 w-4 mr-1" />
                예약 현황
              </div>
              <div className="font-medium">
                {session.current_reservation_count} / {session.max_capacity}
              </div>
            </div>
            <div>
              <div className="flex items-center text-sm text-gray-600 mb-1">
                <Clock className="h-4 w-4 mr-1" />
                수업 시간
              </div>
              <div className="font-medium">{session.duration_minutes}분</div>
            </div>
            <div>
              <div className="text-sm text-gray-600 mb-1">강사</div>
              <div className="font-medium">{instructor?.name || 'N/A'}</div>
            </div>
          </div>
          
          <div className="mt-4 pt-4 border-t grid grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{attendedCount}</div>
              <div className="text-sm text-gray-600">출석</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">{pendingCount}</div>
              <div className="text-sm text-gray-600">미처리</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">{noShowCount}</div>
              <div className="text-sm text-gray-600">노쇼</div>
            </div>
          </div>

          {/* 수업 소개 (description) — 인라인 편집 */}
          <div className="mt-4 pt-4 border-t">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-medium text-gray-700">수업 소개</div>
              {!descEditing && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={startEditDesc}
                  className="text-xs h-7 text-blue-600 hover:bg-blue-50"
                  data-testid="edit-description-btn"
                >
                  {session.notes ? '수정' : '추가'}
                </Button>
              )}
            </div>
            {descEditing ? (
              <div className="space-y-2">
                <textarea
                  value={descDraft}
                  onChange={(e) => setDescDraft(e.target.value)}
                  rows={5}
                  maxLength={2000}
                  placeholder="회원 앱 수업 상세에 표시되는 소개 문구입니다. 장소, 준비물, 대상, 진행 방식 등을 자세히 적어주세요."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical"
                  data-testid="description-textarea"
                  autoFocus
                />
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400">{descDraft.length} / 2000</span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={cancelEditDesc}
                      disabled={descSaving}
                      data-testid="description-cancel-btn"
                    >
                      취소
                    </Button>
                    <Button
                      size="sm"
                      onClick={saveDesc}
                      disabled={descSaving}
                      data-testid="description-save-btn"
                    >
                      {descSaving ? '저장 중...' : '저장'}
                    </Button>
                  </div>
                </div>
              </div>
            ) : session.notes ? (
              <p className="text-sm text-gray-700 whitespace-pre-wrap" data-testid="description-display">
                {session.notes}
              </p>
            ) : (
              <p className="text-sm text-gray-400 italic" data-testid="description-empty">
                아직 입력된 소개가 없습니다. 회원 앱 수업 상세 화면에는 표시되지 않습니다.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Reservations List */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>예약자 목록 ({filteredReservations.length})</CardTitle>
            <div className="flex gap-2">
              <Button onClick={() => setAddReservationDialog(true)} variant="outline" size="sm">
                <Users className="mr-2 h-4 w-4" />
                수기 예약 추가
              </Button>
              <Button onClick={() => setCheckInDialog(true)} size="sm">
                <CheckCircle className="mr-2 h-4 w-4" />
                출석 체크
              </Button>
              <Button onClick={() => setSettlementDialog(true)} variant="outline" size="sm">
                <AlertTriangle className="mr-2 h-4 w-4" />
                노쇼 정산
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                className="pl-10"
                placeholder="회원명, 회원번호, 전화번호로 검색..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3">회원번호</th>
                  <th className="text-left p-3">이름</th>
                  <th className="text-left p-3">전화번호</th>
                  <th className="text-left p-3">이용권</th>
                  <th className="text-left p-3">예약상태</th>
                  <th className="text-left p-3">출석여부</th>
                  <th className="text-left p-3">액션</th>
                </tr>
              </thead>
              <tbody>
                {filteredReservations.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center p-8 text-gray-500">
                      예약이 없습니다
                    </td>
                  </tr>
                ) : (
                  filteredReservations.map((reservation) => (
                    <tr key={reservation._id} className="border-b hover:bg-gray-50">
                      <td className="p-3">{reservation.member?.member_number}</td>
                      <td className="p-3 font-medium">{reservation.member?.name}</td>
                      <td className="p-3">{reservation.member?.phone}</td>
                      <td className="p-3">
                        {(() => {
                          const raw = reservation.member_active_passes || [];
                          if (raw.length === 0) {
                            // 회원의 활성 이용권이 하나도 없으면 예약 시점의 pass만 표시(레거시 폴백)
                            const p = reservation.pass;
                            if (!p) return <span className="text-xs text-gray-500">-</span>;
                            return (
                              <div className="text-xs">
                                <div className="font-medium">{p.name}</div>
                              </div>
                            );
                          }
                          // 기간제 → 횟수권 순서로 정렬
                          const order = { period: 0, count: 1 };
                          const passes = [...raw].sort(
                            (a, b) => (order[a.pass_type] ?? 9) - (order[b.pass_type] ?? 9)
                          );
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          const fmtEnd = (iso) => {
                            if (!iso) return null;
                            const d = new Date(iso);
                            if (Number.isNaN(d.getTime())) return String(iso);
                            const y = d.getFullYear();
                            const m = String(d.getMonth() + 1).padStart(2, '0');
                            const dd = String(d.getDate()).padStart(2, '0');
                            return `${y}.${m}.${dd}`;
                          };
                          const daysLeft = (iso) => {
                            if (!iso) return null;
                            const d = new Date(iso);
                            if (Number.isNaN(d.getTime())) return null;
                            d.setHours(0, 0, 0, 0);
                            return Math.max(0, Math.round((d - today) / 86400000));
                          };
                          return (
                            <div className="text-xs space-y-2" data-testid={`res-pass-info-${reservation._id}`}>
                              {passes.map((p) => {
                                const endLabel = fmtEnd(p.end_date);
                                let subLine = null;
                                if (p.pass_type === 'period') {
                                  const dleft = daysLeft(p.end_date);
                                  subLine = (
                                    <>
                                      {dleft !== null && <>{dleft}일 남음</>}
                                      {endLabel && (
                                        <span className="ml-1">({endLabel})</span>
                                      )}
                                    </>
                                  );
                                } else if (p.pass_type === 'count') {
                                  subLine = (
                                    <>
                                      {p.remaining_count != null && (
                                        <>{p.remaining_count}회 남음</>
                                      )}
                                      {endLabel && (
                                        <span className="ml-1">({endLabel})</span>
                                      )}
                                    </>
                                  );
                                }
                                return (
                                  <div key={p._id}>
                                    <div className="font-medium">{p.name}</div>
                                    {subLine && (
                                      <div className="text-[11px] text-gray-500 leading-tight mt-0.5">
                                        {subLine}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="p-3">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          reservation.status === 'confirmed' ? 'bg-blue-100 text-blue-800' :
                          reservation.status === 'cancelled' ? 'bg-gray-100 text-gray-800' :
                          reservation.status === 'no_show' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {reservation.status === 'confirmed' ? '확정' :
                           reservation.status === 'cancelled' ? '취소' :
                           reservation.status === 'no_show' ? '노쇼' : reservation.status}
                        </span>
                      </td>
                      <td className="p-3">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          reservation.settlement_status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          reservation.settlement_status === 'deducted' ? 'bg-green-100 text-green-800' :
                          reservation.settlement_status === 'refunded' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {reservation.settlement_status === 'pending' ? '대기' :
                           reservation.settlement_status === 'deducted' ? '출석' :
                           reservation.settlement_status === 'refunded' ? '환불' : '면제'}
                        </span>
                      </td>
                      <td className="p-3">
                        <div className="flex gap-1 flex-wrap">
                          {/* 출석 체크 버튼: confirmed이고 pending 또는 refunded일 때 */}
                          {reservation.status === 'confirmed' && 
                           (reservation.settlement_status === 'pending' || reservation.settlement_status === 'refunded') && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-green-600 border-green-600 hover:bg-green-50"
                              onClick={() => handleCheckIn(reservation.member_id)}
                              data-testid={`check-in-btn-${reservation._id}`}
                            >
                              출석 체크
                            </Button>
                          )}
                          
                          {/* 출석 취소 버튼: deducted이고 attendance가 있을 때 */}
                          {reservation.settlement_status === 'deducted' && reservation.attendance && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                              onClick={() => handleRevertAttendance(reservation.attendance._id)}
                              data-testid={`revert-attendance-btn-${reservation._id}`}
                            >
                              출석 취소
                            </Button>
                          )}
                          
                          {/* 노쇼 환불 버튼: deducted이고 attendance가 없을 때 (노쇼 처리된 경우) */}
                          {reservation.settlement_status === 'deducted' && !reservation.attendance && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              onClick={() => handleRefundNoShow(reservation._id, reservation.pass_id)}
                              data-testid={`refund-noshow-btn-${reservation._id}`}
                            >
                              노쇼 환불
                            </Button>
                          )}
                          
                          {/* 예약 취소 버튼: pending 또는 refunded일 때 (deducted가 아닐 때만) */}
                          {reservation.status === 'confirmed' &&
                           (reservation.settlement_status === 'pending' || reservation.settlement_status === 'refunded') && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleCancelReservation(reservation._id)}
                              data-testid={`cancel-reservation-btn-${reservation._id}`}
                            >
                              예약 취소
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* 수업 히스토리 (Activity Timeline) */}
      <Card data-testid="session-history-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5 text-gray-600" />
            수업 히스토리 ({historyEvents.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {historyEvents.length === 0 ? (
            <p className="text-gray-500 text-center py-8" data-testid="session-history-empty">
              아직 기록된 이벤트가 없습니다
            </p>
          ) : (
            <ol className="relative border-l border-gray-200 ml-3 space-y-5" data-testid="session-history-timeline">
              {historyEvents.map((evt) => {
                const meta = ({
                  reservation_created: {
                    label: '예약 생성',
                    Icon: UserPlus,
                    iconBg: 'bg-blue-100',
                    iconColor: 'text-blue-600',
                    dotColor: 'bg-blue-500',
                  },
                  admin_reservation_created: {
                    label: '관리자 예약 추가',
                    Icon: UserPlus,
                    iconBg: 'bg-indigo-100',
                    iconColor: 'text-indigo-600',
                    dotColor: 'bg-indigo-500',
                  },
                  reservation_cancelled: {
                    label: '예약 취소',
                    Icon: XCircle,
                    iconBg: 'bg-gray-100',
                    iconColor: 'text-gray-600',
                    dotColor: 'bg-gray-400',
                  },
                  attendance_checked: {
                    label: '출석 체크',
                    Icon: UserCheck,
                    iconBg: 'bg-green-100',
                    iconColor: 'text-green-600',
                    dotColor: 'bg-green-500',
                  },
                  no_show: {
                    label: '노쇼 처리',
                    Icon: UserX,
                    iconBg: 'bg-red-100',
                    iconColor: 'text-red-600',
                    dotColor: 'bg-red-500',
                  },
                  no_show_refunded: {
                    label: '노쇼 환불',
                    Icon: RotateCcw,
                    iconBg: 'bg-amber-100',
                    iconColor: 'text-amber-700',
                    dotColor: 'bg-amber-500',
                  },
                })[evt.event_type] || {
                  label: evt.event_type,
                  Icon: History,
                  iconBg: 'bg-gray-100',
                  iconColor: 'text-gray-600',
                  dotColor: 'bg-gray-400',
                };

                const Icon = meta.Icon;
                return (
                  <li
                    key={evt._id}
                    className="ml-6"
                    data-testid={`session-history-item-${evt._id}`}
                  >
                    <span
                      className={`absolute -left-[7px] flex items-center justify-center w-3.5 h-3.5 rounded-full ring-4 ring-white ${meta.dotColor}`}
                    />
                    <div className="flex items-start gap-3">
                      <div
                        className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${meta.iconBg}`}
                      >
                        <Icon className={`h-4 w-4 ${meta.iconColor}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          <div className="flex items-center gap-2">
                            <span
                              className={`text-sm font-medium ${meta.iconColor}`}
                              data-testid={`session-history-label-${evt._id}`}
                            >
                              {meta.label}
                            </span>
                            {evt.member_name && (
                              <span className="text-sm text-gray-900 font-medium">
                                {evt.member_name}
                              </span>
                            )}
                          </div>
                          <time
                            className="text-xs text-gray-500 tabular-nums shrink-0"
                            data-testid={`session-history-time-${evt._id}`}
                          >
                            {fmtKstDateTime(evt.timestamp, false)}
                          </time>
                        </div>
                        {evt.description && (
                          <p className="text-xs text-gray-600 mt-1 break-words">
                            {evt.description}
                          </p>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </CardContent>
      </Card>

      {/* Add Reservation Dialog */}
      <Dialog open={addReservationDialog} onOpenChange={(open) => {
        setAddReservationDialog(open);
        if (!open) {
          setMemberSearchQuery('');
          setSearchResults([]);
          setSelectedMember(null);
          setSelectedPass(null);
        }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>수기 예약 추가</DialogTitle>
            <DialogDescription>
              회원을 검색하고 이용권을 선택하여 예약을 추가하세요
            </DialogDescription>
          </DialogHeader>

          {session && new Date(session.scheduled_at) < new Date() && (
            <div
              data-testid="admin-past-session-notice"
              className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
            >
              지난 수업입니다. 관리자 수기 예약은 예약 마감 정책의 예외로 과거 수업에도 추가할 수 있습니다.
            </div>
          )}
          
          <div className="grid gap-4 py-4">
            {/* Member Search */}
            <div className="grid gap-2">
              <Label>회원 검색</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  className="pl-10"
                  placeholder="회원명, 회원번호, 전화번호 입력..."
                  value={memberSearchQuery}
                  onChange={(e) => {
                    setMemberSearchQuery(e.target.value);
                    handleSearchMembers(e.target.value);
                  }}
                />
              </div>
            </div>

            {/* Search Results */}
            {searchResults.length > 0 && !selectedMember && (
              <div className="border rounded-lg max-h-60 overflow-y-auto">
                <div className="text-sm text-gray-600 p-2 bg-gray-50">
                  검색 결과 ({searchResults.length})
                </div>
                {searchResults.map((member) => (
                  <div
                    key={member._id}
                    className="p-3 border-b hover:bg-gray-50 cursor-pointer"
                    onClick={() => {
                      setSelectedMember(member);
                      if (member.active_passes && member.active_passes.length > 0) {
                        setSelectedPass(member.active_passes[0]);
                      }
                    }}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-medium">{member.name}</div>
                        <div className="text-sm text-gray-600">
                          {member.member_number} · {member.phone}
                        </div>
                      </div>
                      <div className="text-sm text-gray-600">
                        이용권 {member.active_passes?.length || 0}개
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Selected Member */}
            {selectedMember && (
              <div className="border rounded-lg p-4 bg-blue-50">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <div className="font-medium text-lg">{selectedMember.name}</div>
                    <div className="text-sm text-gray-600">
                      {selectedMember.member_number} · {selectedMember.phone}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectedMember(null);
                      setSelectedPass(null);
                    }}
                  >
                    변경
                  </Button>
                </div>

                {/* Pass Selection */}
                {selectedMember.active_passes && selectedMember.active_passes.length > 0 ? (
                  <div>
                    <Label className="mb-2 block">이용권 선택</Label>
                    <div className="space-y-2">
                      {selectedMember.active_passes.map((pass) => (
                        <div
                          key={pass._id}
                          className={`p-3 border rounded cursor-pointer transition ${
                            selectedPass?._id === pass._id
                              ? 'border-blue-500 bg-white'
                              : 'border-gray-300 bg-white hover:border-gray-400'
                          }`}
                          onClick={() => setSelectedPass(pass)}
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="font-medium">{pass.name}</div>
                              <div className="text-sm text-gray-600">
                                {pass.pass_type === 'period' ? '기간제' : '횟수제'}
                                {' · '}
                                {new Date(pass.end_date).toLocaleDateString('ko-KR', { timeZone: 'Asia/Seoul' })}까지
                              </div>
                            </div>
                            {pass.pass_type === 'count' && (
                              <div className="text-right">
                                <div className="font-medium text-blue-600">
                                  {pass.remaining_count}회
                                </div>
                                <div className="text-xs text-gray-500">남음</div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-red-600">
                    사용 가능한 이용권이 없습니다
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              onClick={handleAddReservation}
              disabled={!selectedMember || !selectedPass}
            >
              예약 추가
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Check-in Dialog */}
      <Dialog open={checkInDialog} onOpenChange={setCheckInDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>출석 체크</DialogTitle>
            <DialogDescription>
              회원번호 또는 회원 ID를 입력하세요
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="checkInInput">회원번호 (예: M0001)</Label>
              <Input
                id="checkInInput"
                value={checkInInput}
                onChange={(e) => setCheckInInput(e.target.value)}
                placeholder="M0001"
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && checkInInput.trim()) {
                    handleCheckIn(checkInInput.trim());
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button 
              onClick={() => handleCheckIn(checkInInput.trim())}
              disabled={!checkInInput.trim()}
            >
              출석 체크
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settlement Dialog */}
      <Dialog open={settlementDialog} onOpenChange={(open) => {
        setSettlementDialog(open);
        if (!open) setSettlementResult(null);
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>노쇼 정산</DialogTitle>
            <DialogDescription>
              출석하지 않은 예약자를 모두 노쇼 처리하시겠습니까?
            </DialogDescription>
          </DialogHeader>
          
          {/* 처리 대상 안내 */}
          {!settlementResult && (
            <div className="py-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  <strong>노쇼 처리 대상:</strong> {pendingCount}명
                </p>
                <p className="text-xs text-yellow-600 mt-1">
                  미출석 예약자의 이용권이 자동 차감됩니다.
                </p>
              </div>
              
              {pendingCount === 0 && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-3">
                  <p className="text-sm text-gray-600">
                    노쇼 처리할 예약자가 없습니다.
                  </p>
                </div>
              )}
            </div>
          )}
          
          {/* 처리 결과 */}
          {settlementResult && (
            <div className="py-4">
              <div className={`rounded-lg p-4 ${
                settlementResult.processed > 0 ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'
              }`}>
                <p className={`font-medium ${settlementResult.processed > 0 ? 'text-green-800' : 'text-gray-600'}`}>
                  {settlementResult.processed > 0 
                    ? `✓ 노쇼 정산 완료: ${settlementResult.processed}명 처리됨`
                    : '노쇼 처리할 예약자가 없습니다.'}
                </p>
              </div>
              
              {settlementResult.details && settlementResult.details.length > 0 && (
                <div className="space-y-2 mt-4">
                  <p className="text-sm font-medium text-gray-700">처리 내역:</p>
                  {settlementResult.details.map((detail, idx) => (
                    <div key={idx} className={`p-2 rounded text-sm ${
                      detail.status === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                    }`}>
                      {detail.member_name} ({detail.member_number})
                      {detail.error && <div className="text-xs mt-1">오류: {detail.error}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setSettlementDialog(false);
              setSettlementResult(null);
            }}>
              {settlementResult ? '닫기' : '취소'}
            </Button>
            {!settlementResult && pendingCount > 0 && (
              <Button 
                onClick={handleNoShowSettlement}
                className="bg-red-600 hover:bg-red-700"
              >
                노쇼 정산 실행
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Session Confirmation Dialog */}
      <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <DialogContent data-testid="delete-session-dialog">
          <DialogHeader>
            <DialogTitle>수업 삭제</DialogTitle>
            <DialogDescription>
              삭제 범위를 선택해주세요. 이 작업은 되돌릴 수 없습니다.
            </DialogDescription>
          </DialogHeader>

          <div className="py-2 space-y-3">
            <div className="rounded-lg border p-4 bg-gray-50">
              <div className="font-medium">{session.snapshot_name}</div>
              <div className="text-sm text-gray-600 mt-1">
                {new Date(session.scheduled_at).toLocaleString('ko-KR', {
                  timeZone: 'Asia/Seoul',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </div>
            </div>

            {/* Scope Selection */}
            <div className="space-y-2">
              <label
                className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition ${
                  deleteScope === 'single' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                }`}
                data-testid="delete-scope-single"
              >
                <input
                  type="radio"
                  name="deleteScope"
                  value="single"
                  checked={deleteScope === 'single'}
                  onChange={() => setDeleteScope('single')}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="font-medium text-sm">이 수업만 삭제</div>
                  <div className="text-xs text-gray-600 mt-0.5">
                    현재 표시 중인 수업 1개만 삭제합니다
                  </div>
                  {reservations.length > 0 && (
                    <div className="text-xs text-yellow-700 mt-1">
                      예약자 {reservations.length}명이 있어 삭제할 수 없습니다
                    </div>
                  )}
                </div>
              </label>

              <label
                className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition ${
                  deleteScope === 'series' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                }`}
                data-testid="delete-scope-series"
              >
                <input
                  type="radio"
                  name="deleteScope"
                  value="series"
                  checked={deleteScope === 'series'}
                  onChange={() => setDeleteScope('series')}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="font-medium text-sm">이 수업과 향후 모든 수업 삭제</div>
                  <div className="text-xs text-gray-600 mt-0.5">
                    같은 이름의 이후 일정을 한 번에 삭제합니다
                  </div>
                  {seriesPreview ? (
                    <div className="text-xs mt-1 space-y-0.5">
                      <div className="text-gray-700">
                        총 <span className="font-semibold">{seriesPreview.total}개</span>의 향후 수업 대상
                      </div>
                      <div className="text-blue-700">
                        삭제 가능: <span className="font-semibold">{seriesPreview.deletable}개</span>
                        {seriesPreview.blocked > 0 && (
                          <span className="text-yellow-700 ml-2">
                            · 예약자 있는 <span className="font-semibold">{seriesPreview.blocked}개</span> 제외
                          </span>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="text-xs text-gray-400 mt-1">집계 중...</div>
                  )}
                </div>
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(false)}>
              취소
            </Button>
            <Button
              onClick={handleDeleteSession}
              disabled={
                deleting ||
                (deleteScope === 'single' && reservations.length > 0) ||
                (deleteScope === 'series' && (!seriesPreview || seriesPreview.deletable === 0))
              }
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-session-btn"
            >
              {deleting
                ? '삭제 중...'
                : deleteScope === 'series' && seriesPreview
                  ? `${seriesPreview.deletable}개 삭제`
                  : '삭제'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SessionDetail;
